import { useState, useMemo, useEffect } from "react";
import { useResponsive } from "../../hooks/useResponsive";

const PAGE_SIZE = 10;

export default function FalecidosModal({ isOpen, onClose, falecidos, onSelect }) {
  const { isDesktop } = useResponsive();
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (falecidos) {
      setData(falecidos);
      setCurrentPage(1);
      setSelectedIndex(null);
      setSearch("");
    }
  }, [falecidos]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? data.filter((f) => (f.nome_completo || "").toLowerCase().includes(q)) : data;
  }, [data, search]);

  const totalPages = Math.ceil((filtered.length || 0) / PAGE_SIZE);

  const currentData = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, currentPage]);

  if (!isOpen) return null;

  const formatDate = (d) => {
    if (!d) return "—";
    const [y, m, day] = d.split("-");
    return `${day}/${m}/${y}`;
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
    setSelectedIndex(null);
  };

  const handleSelect = (localIndex) => {
    const globalIndex = (currentPage - 1) * PAGE_SIZE + localIndex;
    setSelectedIndex(globalIndex);
  };

  const handleConfirm = () => {
    if (selectedIndex !== null) {
      onSelect(filtered[selectedIndex]);
      onClose();
    }
  };

  return (
    <div style={overlay}>
      <div style={modal}>

        {/* HEADER */}
        <div style={header}>
          <h3 style={{ margin: 0, fontSize: 15, fontFamily: "'DM Sans', sans-serif" }}>
            Falecidos — {filtered.length} encontrado{filtered.length !== 1 ? "s" : ""}
          </h3>
          <button onClick={onClose} style={closeButton}>✕</button>
        </div>

        {/* BUSCA */}
        <div style={{ padding: "10px 16px", borderBottom: "1px solid #f0f0f0" }}>
          <input
            type="text"
            placeholder="Buscar por nome..."
            value={search}
            onChange={handleSearchChange}
            style={{
              width: "100%",
              border: "none",
              borderBottom: "1.5px solid #e0e0e0",
              outline: "none",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 14,
              color: "#444",
              background: "transparent",
              padding: "4px 0",
              boxSizing: "border-box",
            }}
          />
        </div>

        {/* CONTEÚDO — tabela no desktop, cards em mobile/tablet */}
        <div style={{ flex: 1, overflow: "auto" }}>
          {/* No mobile/tablet exibe todos sem paginação; no desktop usa página atual */}
          {isDesktop ? (
            /* ── TABELA (desktop) ── */
            <div className="table-scroll-x">
              <table style={table}>
                <thead>
                  <tr>
                    <th style={{ ...th, width: "40%" }}>Nome completo</th>
                    <th style={{ ...th, width: "20%" }}>Apelido</th>
                    <th style={{ ...th, width: "20%", textAlign: "center" }}>Nascimento</th>
                    <th style={{ ...th, width: "20%", textAlign: "center" }}>Falecimento</th>
                  </tr>
                </thead>
                <tbody>
                  {currentData.map((falecido, localIndex) => {
                    const globalIndex = (currentPage - 1) * PAGE_SIZE + localIndex;
                    const isSelected = globalIndex === selectedIndex;
                    return (
                      <tr
                        key={falecido.id ?? localIndex}
                        onClick={() => handleSelect(localIndex)}
                        onDoubleClick={() => { handleSelect(localIndex); onSelect(falecido); onClose(); }}
                        style={{
                          background: isSelected
                            ? "rgba(242,166,108,0.25)"
                            : localIndex % 2 === 0
                            ? "rgba(242,166,108,0.07)"
                            : "#fff",
                          cursor: "pointer",
                          outline: isSelected ? "2px solid #E8956A" : "none",
                        }}
                      >
                        <td style={td}>{truncate(falecido.nome_completo, 40)}</td>
                        <td style={td}>{truncate(falecido.apelido, 25) || "—"}</td>
                        <td style={{ ...td, textAlign: "center" }}>{formatDate(falecido.data_nascimento)}</td>
                        <td style={{ ...td, textAlign: "center" }}>{formatDate(falecido.data_falecimento)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            /* ── CARDS (mobile / tablet) ── */
            <div style={{ padding: "8px 0" }}>
              {filtered.map((falecido, localIndex) => {
                const isSelected = localIndex === selectedIndex;
                return (
                  <button
                    key={falecido.id ?? localIndex}
                    onClick={() => {
                      onSelect(falecido);
                      onClose();
                    }}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      border: "none",
                      borderBottom: "1px solid #f0ebe6",
                      borderLeft: isSelected ? "4px solid #E8956A" : "4px solid transparent",
                      background: isSelected
                        ? "rgba(242,166,108,0.12)"
                        : localIndex % 2 === 0
                        ? "rgba(242,166,108,0.04)"
                        : "#fff",
                      padding: "14px 16px",
                      cursor: "pointer",
                      transition: "background 0.15s ease",
                    }}
                  >
                    {/* Nome */}
                    <div
                      style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontWeight: 700,
                        fontSize: 15,
                        color: "#2d2d2d",
                        lineHeight: 1.3,
                      }}
                    >
                      {falecido.nome_completo}
                    </div>

                    {/* Apelido */}
                    {falecido.apelido && (
                      <div
                        style={{
                          fontFamily: "'DM Sans', sans-serif",
                          fontSize: 13,
                          color: "#999",
                          marginTop: 3,
                          fontStyle: "italic",
                        }}
                      >
                        "{falecido.apelido}"
                      </div>
                    )}

                    {/* Datas */}
                    <div
                      style={{
                        display: "flex",
                        gap: 20,
                        marginTop: 8,
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 12,
                        color: "#aaa",
                      }}
                    >
                      <span>★ {formatDate(falecido.data_nascimento)}</span>
                      <span>✝ {formatDate(falecido.data_falecimento)}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* FOOTER — paginação só no desktop */}
        {isDesktop && (
          <div style={footer}>
            <button
              onClick={() => setCurrentPage((p) => p - 1)}
              disabled={currentPage === 1}
              style={navButton}
            >
              ◀
            </button>

            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#666" }}>
              {currentPage} / {totalPages || 1}
            </span>

            <button
              onClick={() => setCurrentPage((p) => p + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              style={navButton}
            >
              ▶
            </button>

            <button
              onClick={handleConfirm}
              disabled={selectedIndex === null}
              style={{ ...selectButton, opacity: selectedIndex === null ? 0.5 : 1 }}
            >
              Selecionar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function truncate(text, max) {
  if (!text) return "";
  return text.length > max ? text.substring(0, max) + "..." : text;
}

/* ── ESTILOS ── */

const overlay = {
  position: "fixed",
  top: 0, left: 0,
  width: "100%", height: "100%",
  background: "rgba(0,0,0,0.45)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1000,
};

const modal = {
  width: "min(95vw, 750px)",
  maxHeight: "90vh",
  background: "#fff",
  borderRadius: "14px",
  overflow: "hidden",
  boxShadow: "0 16px 48px rgba(0,0,0,0.22)",
  display: "flex",
  flexDirection: "column",
};

const header = {
  background: "linear-gradient(135deg, #F2A66C 0%, #E8956A 100%)",
  padding: "14px 20px",
  color: "#fff",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const closeButton = {
  background: "transparent",
  border: "none",
  color: "#fff",
  fontSize: "20px",
  cursor: "pointer",
  lineHeight: 1,
  padding: "0 4px",
};

const table = {
  width: "100%",
  borderCollapse: "collapse",
};

const th = {
  padding: "10px 12px",
  textAlign: "left",
  background: "#f5f5f5",
  fontFamily: "'DM Sans', sans-serif",
  fontSize: 13,
  fontWeight: 600,
  color: "#666",
};

const td = {
  padding: "10px 12px",
  fontSize: "14px",
  fontFamily: "'DM Sans', sans-serif",
  color: "#444",
};

const footer = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "10px 16px",
  borderTop: "1px solid #f0f0f0",
  gap: 8,
};

const navButton = {
  background: "none",
  border: "1px solid #ddd",
  borderRadius: 6,
  padding: "6px 12px",
  cursor: "pointer",
  fontFamily: "'DM Sans', sans-serif",
  fontSize: 13,
  color: "#555",
};

const selectButton = {
  background: "#E8956A",
  border: "none",
  padding: "8px 16px",
  color: "#fff",
  borderRadius: "8px",
  cursor: "pointer",
  fontFamily: "'DM Sans', sans-serif",
  fontWeight: 600,
  fontSize: 14,
};
